import React, { useMemo } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { PineParseIssue, ReportStrategyPineFields } from "../../types/reportStrategyPine";
import {
  buildStrategyPineParseSummaryNarrative,
  type PineUxCopy,
} from "../../utils/pineIrUi";
import { STRATEGY_PINE_SECTION_DESC_CLASS, STRATEGY_PINE_SECTION_LABEL_CLASS } from "./StrategyPineSectionCard";
import {
  STRATEGY_PINE_ALERT_CLASS,
  StrategyPineAlertIcon,
  strategyPineAlertVariant,
} from "./strategyPineAlertUi";

export type StrategyPineParseSummaryAlertProps = {
  fields: ReportStrategyPineFields | null | undefined;
  issues?: PineParseIssue[];
  parseStatusUx?: Record<string, PineUxCopy> | null;
  injectReadinessUx?: Record<string, PineUxCopy> | null;
  exitHookReadinessUx?: Record<string, PineUxCopy> | null;
};

export function StrategyPineParseSummaryAlert({
  fields,
  issues = [],
  parseStatusUx,
  injectReadinessUx,
  exitHookReadinessUx,
}: StrategyPineParseSummaryAlertProps): React.ReactElement | null {
  const summary = useMemo(
    () =>
      fields
        ? buildStrategyPineParseSummaryNarrative({
            fields,
            issues,
            parseStatusUx,
            injectReadinessUx,
            exitHookReadinessUx,
          })
        : null,
    [exitHookReadinessUx, fields, injectReadinessUx, issues, parseStatusUx]
  );

  if (!summary) return null;

  return (
    <Alert variant={strategyPineAlertVariant(summary.tone)} className={STRATEGY_PINE_ALERT_CLASS}>
      <StrategyPineAlertIcon tone={summary.tone} />
      <AlertTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{summary.title}</AlertTitle>
      {summary.description || summary.fixItems?.length ? (
        <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
          {summary.description ? <p>{summary.description}</p> : null}
          {summary.fixItems?.length ? (
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {summary.fixItems.map((item, index) => (
                <li key={`${index}-${item.slice(0, 24)}`}>{item}</li>
              ))}
            </ul>
          ) : null}
        </AlertDescription>
      ) : null}
    </Alert>
  );
}
